// Lazy-init App Insights wrapper. Fetches the connection string from
// GET /api/config (unauthenticated) so that per-customer builds don't need to
// bake in a conn string at build time — the Function App's
// APPLICATIONINSIGHTS_CONNECTION_STRING env var is the single source of truth
// for both backend and SPA telemetry.
//
// The same ApplicationInsights instance serves:
//   - trackAuthEvent (auth.* events fired from useAuth / AuthProvider)
//   - getAppInsights() (used by sessionHeartbeat to send usage.heartbeat)
//
// Design notes:
//   - trackAuthEvent is fire-and-forget. If init fails, conn string is empty,
//     or the SDK crashes, we swallow and log to console.info. Auth flow must
//     never depend on telemetry.
//   - First trackAuthEvent or getAppInsights call triggers init; subsequent
//     calls reuse the same ApplicationInsights instance.
//   - All auth events share the customDimensions prefix `auth.` so they're
//     easy to slice in the customEvents table:
//       customEvents | where name startswith "auth."
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

export type AuthEventName =
  | 'auth.silent_sso_attempt'
  | 'auth.silent_sso_success'
  | 'auth.silent_sso_failure'
  | 'auth.popup_fallback'
  | 'auth.popup_timeout'
  | 'auth.token_refresh_interactive'
  | 'auth.admin_consent_required'
  | 'auth.teams_user_from_token'
  | 'auth.teams_user_unresolved';

export type AuthEventProperties = {
  duration_ms?: number;
  inTeams?: boolean;
  errorCode?: string;
  errorSubcode?: string; // AADSTS-* codes, extracted from the message
  path?: 'silent' | 'interactive';
};

let appInsights: ApplicationInsights | null = null;
let initPromise: Promise<void> | null = null;
let initFailed = false;

// Auth events fire in the first ~100ms of page load, but /api/config + SDK
// init typically take 500-1000ms. Without a queue those early events were
// silently dropped: the caller hit the `if (!appInsights) return;` path and
// nothing reached the ingestion endpoint. Buffer them here and flush on init
// success. Cap the queue so a never-resolving init can't bloat memory.
type QueuedEvent = { name: AuthEventName; properties: AuthEventProperties };
const pending: QueuedEvent[] = [];
const PENDING_MAX = 50;

function sendEvent(name: AuthEventName, properties: AuthEventProperties): void {
  if (!appInsights) return;
  try {
    appInsights.trackEvent({
      name,
      properties: {
        ...properties,
        // Normalise boolean to string for easier KQL filtering
        inTeams: properties.inTeams === undefined ? undefined : String(properties.inTeams),
      },
    });
  } catch {
    // Telemetry must never break the auth flow.
  }
}

function flushPending(): void {
  while (pending.length > 0) {
    const ev = pending.shift()!;
    sendEvent(ev.name, ev.properties);
  }
}

async function init(): Promise<void> {
  if (appInsights || initFailed) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const res = await fetch('/api/config', { method: 'GET' });
      if (!res.ok) {
        initFailed = true;
        pending.length = 0; // drop buffered events
        return;
      }
      const { appInsightsConnectionString } = (await res.json()) as {
        appInsightsConnectionString?: string;
      };
      if (!appInsightsConnectionString) {
        // No conn string = dev build or not-yet-deployed. Silently disable.
        initFailed = true;
        pending.length = 0;
        return;
      }
      appInsights = new ApplicationInsights({
        config: {
          connectionString: appInsightsConnectionString,
          // Auto-track page views + the common browser errors. Keep
          // enabled — we want this for general SPA health too.
          enableAutoRouteTracking: true,
          disableAjaxTracking: false,
          // Don't send cookies with telemetry beacons; the Function App's
          // rate limiter doesn't apply (telemetry goes to Azure ingestion,
          // not /api/*) but less metadata = less privacy surface.
          disableCookiesUsage: true,
        },
      });
      appInsights.loadAppInsights();
      appInsights.trackPageView();
      flushPending();
    } catch (err) {
      initFailed = true;
      pending.length = 0;
      console.info('[auth-telemetry] init failed; events will be console-only:', err);
    }
  })();

  return initPromise;
}

// Extract AADSTS<digits> from an error message — MSAL and Teams SDK both
// surface the Entra error code inside the message string. Example:
//   "AADSTS65001: The user or administrator has not consented..."
export function extractAadstsCode(message: string | undefined): string | undefined {
  if (!message) return undefined;
  const match = message.match(/AADSTS\d+/);
  return match?.[0];
}

// Shared handle to the underlying SDK instance for non-auth consumers
// (e.g. sessionHeartbeat). Resolves to the live instance once init completes,
// or to null if init failed / no conn string is available (dev build).
export async function getAppInsights(): Promise<ApplicationInsights | null> {
  await init();
  return appInsights;
}

export function trackAuthEvent(name: AuthEventName, properties: AuthEventProperties = {}): void {
  // Kick off init lazily; don't block the caller on network.
  void init();

  // Console mirror for local dev + when App Insights init fails. Cheap and
  // useful for tailing `pnpm dev` output.
  console.info(`[auth] ${name}`, properties);

  if (appInsights) {
    sendEvent(name, properties);
    return;
  }

  // Init still in flight (or queued to start). Buffer until flushPending()
  // fires on init success. If init fails permanently, the queue is dropped
  // so we don't leak memory on a broken page session.
  if (!initFailed && pending.length < PENDING_MAX) {
    pending.push({ name, properties });
  }
}
